import { z } from 'zod'
import { idSchema, isoDateTimeSchema, optionalText, text } from './common.js'

/**
 * Where parts and liquids physically live: a rack holds levels directly, and
 * a level is where things are actually allocated. One self-referencing shape
 * for both rather than two separate tables — see the schema.ts comment on
 * storage_nodes for why.
 */
export const STORAGE_NODE_KINDS = ['rack', 'level'] as const
export type StorageNodeKind = (typeof STORAGE_NODE_KINDS)[number]
export const storageNodeKindSchema = z.enum(STORAGE_NODE_KINDS)

/** The kind a CHILD of this kind must be. Null for a level: nothing nests under it. */
export const STORAGE_CHILD_KIND: Record<StorageNodeKind, StorageNodeKind | null> = {
  rack: 'level',
  level: null,
}

const storageNodeFields = {
  name: text(120),
  position: z.number().int().min(0).default(0),
  description: optionalText(2000),
}

export const storageNodeInputSchema = z.object(storageNodeFields)
export type StorageNodeInput = z.infer<typeof storageNodeInputSchema>

export const storageNodeSchema = z.object({
  ...storageNodeFields,
  id: idSchema,
  kind: storageNodeKindSchema,
  parentId: idSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type StorageNode = z.infer<typeof storageNodeSchema>

const storageAllocationFields = {
  catalogItemId: idSchema.nullable().default(null),
  itemName: text(200),
  quantity: z.number().int().min(0).default(1),
  notes: optionalText(2000),
}

export const storageAllocationInputSchema = z.object(storageAllocationFields)
export type StorageAllocationInput = z.infer<typeof storageAllocationInputSchema>

export const storageAllocationPhotoSchema = z.object({
  id: idSchema,
})
export type StorageAllocationPhoto = z.infer<typeof storageAllocationPhotoSchema>

export const storageAllocationSchema = z.object({
  ...storageAllocationFields,
  id: idSchema,
  levelId: idSchema,
  /** Photos of this specific item, not the level it sits on. Server-owned, in upload order; each is added or removed independently rather than one replacing another. */
  photos: z.array(storageAllocationPhotoSchema),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type StorageAllocation = z.infer<typeof storageAllocationSchema>

/** A level, with everything allocated to it. */
export interface StorageLevel extends StorageNode {
  kind: 'level'
  allocations: StorageAllocation[]
}

/** A rack, with its levels nested — the shape GET /storage/tree returns. */
export interface StorageRack extends StorageNode {
  kind: 'rack'
  levels: StorageLevel[]
}
